import { makeAutoObservable, runInAction } from "mobx";
import { t, translate } from "../../I18n";
import { ErrorBox, TextFieldController } from "../../form";
import { WithdrawalEmailModel, WithdrawalPhoneModel, } from "../../ordering";
import { Tracking } from "../../tracking";
/**
 * Drives the marketing-consent withdrawal form served on `/zgody-marketingowe`.
 * Replaces the legacy PureScript `Restaumatic.Site.Components.Consents` widget.
 */
export class MarketingConsentWithdrawalController {
    /**
     * Builds a controller with its field models seeded from `storageManager`.
     * Prefer this over the constructor, which expects the validation models to be
     * supplied ready-made.
     */
    static create(params) {
        var _a, _b;
        const stored = params.storageManager.restore();
        return new MarketingConsentWithdrawalController({
            accountId: params.accountId,
            withdrawalService: params.withdrawalService,
            storageManager: params.storageManager,
            emailModel: new WithdrawalEmailModel((_a = stored === null || stored === void 0 ? void 0 : stored.email) !== null && _a !== void 0 ? _a : ""),
            phoneModel: new WithdrawalPhoneModel((_b = stored === null || stored === void 0 ? void 0 : stored.phone) !== null && _b !== void 0 ? _b : ""),
        });
    }
    constructor(params) {
        this._status = { type: "Editing" };
        this.accountId = params.accountId;
        this.withdrawalService = params.withdrawalService;
        this.storageManager = params.storageManager;
        this.emailModel = params.emailModel;
        this.phoneModel = params.phoneModel;
        this.email = new TextFieldController({
            initialValue: this.emailModel.value,
            onUpdate: (value) => {
                this.emailModel.value = value;
                this.persist();
            },
            errorBox: new ErrorBox({
                domainObjects: [this.emailModel],
                errorMessages: {
                    InvalidEmail: translate(t.validations.invalid_email),
                },
            }),
        });
        this.phone = new TextFieldController({
            initialValue: this.phoneModel.value,
            onUpdate: (value) => {
                this.phoneModel.value = value;
                this.persist();
            },
            errorBox: new ErrorBox({
                domainObjects: [this.phoneModel],
                errorMessages: {
                    InvalidPhoneNumber: translate(t.validations.invalid_phone),
                },
            }),
        });
        makeAutoObservable(this);
    }
    persist() {
        this.storageManager.store({
            email: this.emailModel.value,
            phone: this.phoneModel.value,
        });
    }
    get status() {
        return this._status;
    }
    get isSuccess() {
        return this._status.type === "Success";
    }
    get isSubmitting() {
        return this._status.type === "Submitting";
    }
    get hasNetworkError() {
        return this._status.type === "NetworkError";
    }
    /**
     * A withdrawal request can only be submitted when both fields are well-formed
     * and at least one contact channel was provided (mirrors the legacy widget,
     * whose submit was disabled until the form produced a `Just` request).
     */
    get canSubmit() {
        const bothEmpty = this.emailModel.email.isEmpty && this.phoneModel.phone.isEmpty;
        return (this.email.isValid &&
            this.phone.isValid &&
            !bothEmpty &&
            !this.isSubmitting);
    }
    async submit() {
        this.email.blur();
        this.phone.blur();
        if (!this.canSubmit) {
            return;
        }
        runInAction(() => {
            this._status = { type: "Submitting" };
        });
        const success = await this.withdrawalService.withdraw({
            accountId: this.accountId,
            email: this.emailModel.email,
            phone: this.phoneModel.phone,
        });
        runInAction(() => {
            this._status = success ? { type: "Success" } : { type: "NetworkError" };
        });
        if (success) {
            Tracking.trackEvent({ tag: "MarketingConsentWithdrawn" });
        }
    }
}
